import { useState, useMemo } from 'react';
import { SlowestPayer } from '@dill/dill-shared';

interface SlowestPayersTableProps {
  data: SlowestPayer[];
}

function SlowestPayersTable({ data }: SlowestPayersTableProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const paginatedData = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  }, [data, currentPage]);

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const getDSOColor = (dso: number) => {
    if (dso <= 30) return 'text-green-600';
    if (dso <= 45) return 'text-yellow-600';
    if (dso <= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        No slowest payers data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Rank
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Customer
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Avg DSO
              </th>
              {/* <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"> */}
              {/*   Outstanding */}
              {/* </th> */}
              {/* <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"> */}
              {/*   Last Payment */}
              {/* </th> */}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paginatedData.map((payer) => (
              <tr key={payer.customerId} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-sm text-gray-500">#{payer.rank}</td>
                <td className="px-3 py-2">
                  <div className="text-sm font-medium text-gray-900">{payer.customerName}</div>
                  <div className="text-xs text-gray-500">{payer.customerId}</div>
                </td>
                <td className={`px-3 py-2 text-sm font-medium ${getDSOColor(payer.averageDSO)}`}>
                  {payer.averageDSO.toFixed(1)} days
                </td>
                {/* <td className="px-3 py-2 text-sm text-gray-900"> */}
                {/*   ${payer.totalOutstanding.toLocaleString()} */}
                {/* </td> */}
                {/* <td className="px-3 py-2 text-sm text-gray-500"> */}
                {/*   {payer.lastPaymentDate */}
                {/*     ? new Date(payer.lastPaymentDate).toLocaleDateString() */}
                {/*     : 'Never'} */}
                {/* </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            Showing {currentPage * itemsPerPage + 1} -{' '}
            {Math.min((currentPage + 1) * itemsPerPage, data.length)} of {data.length}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              Prev
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage >= totalPages - 1}
              className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SlowestPayersTable;
